// Centralized URL builders for assets served by the API host.
//
// Replaces fragile patterns like `VITE_API_URL.replace('/api', '')`, which
// silently corrupt URLs when the hostname itself contains "api"
// (e.g. https://api.vagasbrusque.com.br/api → "https:/.vagasbrusque.com.br/api"
// because String.prototype.replace removes only the first match).

const FALLBACK_API_ORIGIN = 'https://api.vagasbrusque.com.br';

/**
 * Returns the API origin (scheme://host[:port]) without `/api` suffix.
 * Used to build asset URLs from server-relative paths like "/storage/...".
 */
export function apiOrigin(): string {
  const env = (import.meta as unknown as { env?: Record<string, string> }).env;
  const apiUrl = env?.VITE_API_URL;
  if (apiUrl && /^https?:\/\//i.test(apiUrl)) {
    try {
      return new URL(apiUrl).origin;
    } catch {
      /* fall through */
    }
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return FALLBACK_API_ORIGIN;
}

/**
 * Builds an absolute asset URL from a server-relative path.
 * - Absolute URLs (http/https) are returned unchanged.
 * - data: URIs are returned unchanged.
 * - Empty / nullish input returns ''.
 */
export function assetUrl(path: string | null | undefined): string {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  if (path.startsWith('data:')) return path;
  const origin = apiOrigin();
  return `${origin}${path.startsWith('/') ? '' : '/'}${path}`;
}

function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

export function jobPublicPath(job: { id?: number | string; title?: string; slug?: string | null }): string {
  const id = job.id ? String(job.id) : '';
  const titleSlug = slugify(job.title || '');
  const baseSlug = titleSlug || slugify(job.slug || '') || 'vaga';
  return `/vaga/${id ? `${baseSlug}-${id}` : baseSlug}`;
}

export function jobPublicUrl(job: { id?: number | string; title?: string; slug?: string | null }, origin?: string): string {
  const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base}${jobPublicPath(job)}`;
}
